import { captchaTokenAtom } from '@/store/home';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ENVIRONMENT } from '@/lib/config';

interface TurnstileProps {
  containerId?: string;
  className?: string;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string,
        options: { sitekey: string; callback: (token: string) => void },
      ) => number;
      remove: (widgetId: number) => void;
      reset: (widgetId: number) => void;
    };
  }
}

export const TurnstileCaptcha: React.FC<TurnstileProps> = ({ containerId = 'turnstile-container', className }) => {
  const setCaptcha = useSetAtom(captchaTokenAtom);
  const router = useRouter(); // Sử dụng hook để theo dõi URL hiện tại

  const [widgetId, setWidgetId] = useState<number | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const scriptId = useMemo(() => {
    return `turnstile-script-${containerId}`;
  }, [containerId]);

  // Hàm để render Turnstile
  const renderTurnstile = useCallback(() => {
    const { turnstile } = window;
    if (ENVIRONMENT.CAPTCHA_SITE_KEY && turnstile) {
      // Xóa widget cũ nếu tồn tại
      if (widgetId !== null) {
        turnstile.remove(widgetId);
      }

      // Render widget mới
      const id = turnstile.render(`#${containerId}`, {
        sitekey: ENVIRONMENT.CAPTCHA_SITE_KEY,
        callback: (token) => {
          setCaptcha(token);
        },
        'refresh-expired': 'auto', // Tự động làm mới token khi hết hạn
      });
      setWidgetId(id);
    }
  }, []);

  // Tải script Turnstile
  useEffect(() => {
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => {
        setIsScriptLoaded(true);
      };

      document.head.appendChild(script);
    } else {
      setIsScriptLoaded(true);
    }

    // Cleanup function
    return () => {
      if (widgetId !== null) {
        const { turnstile } = window;
        if (turnstile) {
          turnstile.remove(widgetId);
        }
      }
    };
  }, [scriptId]);

  // Render Turnstile khi script đã load
  useEffect(() => {
    if (isScriptLoaded) {
      renderTurnstile();
    }
  }, [isScriptLoaded, renderTurnstile]);

  // Tạo lại token khi URL thay đổi
  useEffect(() => {
    if (isScriptLoaded && widgetId !== null) {
      const { turnstile } = window;
      if (turnstile) {
        turnstile.reset(widgetId);
      }
    }
  }, [router.pathname]);

  return (
    <div className={className || 'fixed bottom-2 right-[-150px] hover:right-0 transition-all duration-300 ease-in-out cursor-pointer'}>
      <div id={containerId} />
    </div>
  );
};
